import { useState } from "react";

import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	Grid2,
	IconButton,
	Menu,
	MenuItem,
	Typography,
} from "@mui/material";
import classNames from "classnames";
import { BsThreeDots } from "react-icons/bs";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import { apis, callApi } from "../../../../../api";
import { formatTimeRange } from "../../../../../utils";
import { TFlightLog } from "../../../../../@types";

export type TListFlightLogsProps = {
	flightLogs: TFlightLog[];
	deleteFlightLogs: (id: number) => void;
};

export function ListFlightLogs(props: TListFlightLogsProps) {
	const params = useParams();
	const flightId = params.id;
	const navigate = useNavigate();

	type TState = {
		optionButtonAnchor: HTMLElement | null;
		selectedItemId: number | null;
		confirmDialogOpen: boolean;
	};
	const [state, setState] = useState<TState>({
		optionButtonAnchor: null,
		selectedItemId: null,
		confirmDialogOpen: false,
	});

	const onClickAskForDeletingFlightLog = () => {
		setState((prevState) => ({
			...prevState,
			optionButtonAnchor: null,
			confirmDialogOpen: true,
		}));
	};

	const onClickCloseConfirmDialog = () => {
		setState((prevState) => ({
			...prevState,
			selectedItemId: null,
			confirmDialogOpen: false,
		}));
	};

	const onClickDeleteFlightLog = async () => {
		try {
			const id = state.selectedItemId;
			if (!id) {
				return;
			}

			setState((prevState) => ({
				...prevState,
				selectedItemId: null,
				optionButtonAnchor: null,
				confirmDialogOpen: false,
			}));

			await callApi({
				method: "DELETE",
				url: apis.MY_FLIGHT_LOGS,
				body: {
					sortKey: state.selectedItemId,
				},
			});
			toast.success("Flight is deleted!");

			props.deleteFlightLogs(id);
		} catch (error) {}
	};

	return (
		<Grid2
			size={{ xs: 12, sm: 12, md: 3, lg: 3, xl: 3 }}
			className="flight-logs-list">
			{props.flightLogs.map((flightLog) => {
				const { dateText, timeText } = formatTimeRange(
					Number(flightLog.startTime),
					Number(flightLog.endTime),
				);
				const duration =
					Number(flightLog.endTime) - Number(flightLog.startTime);
				const hours = Math.floor(duration / (60 * 60 * 1000));
				const minutes = Math.round(
					(duration - hours * 60 * 60 * 1000) / (60 * 1000),
				);
				let durationText = "";
				if (hours === 1) {
					durationText += `${hours} hour`;
				} else if (hours > 1) {
					durationText += `${hours} hours`;
				}
				if (minutes === 1) {
					durationText += ` ${minutes} minute`;
				} else if (minutes > 1) {
					durationText += ` ${minutes} minutes`;
				}

				durationText = durationText.trim();
				if (durationText.length === 0) {
					durationText = "0 minutes";
				}

				return (
					<div
						key={flightLog.id}
						className={classNames("flight-log-item", {
							selected: flightId === flightLog.id,
						})}
						onClick={() => {
							navigate(`/flight-logs/${flightLog.id}`);
						}}>
						<Typography className="normal-text bold">{dateText}</Typography>
						<Typography className="normal-text">{timeText}</Typography>
						<Typography className="normal-text">{durationText}</Typography>
						<div
							className={classNames("menu-button-container", {
								show: state.selectedItemId === flightLog.id,
							})}>
							<IconButton
								className="icon-button menu-button"
								onClick={(e) => {
									e.stopPropagation();
									setState((prevState) => ({
										...prevState,
										optionButtonAnchor: e.currentTarget,
										selectedItemId: flightLog.id ?? null,
									}));
								}}>
								<BsThreeDots />
							</IconButton>
						</div>
					</div>
				);
			})}
			{props.flightLogs.length === 0 && (
				<Typography className="normal-text" marginTop="16px" textAlign="center">
					You don't have any flights yet.
				</Typography>
			)}
			<Menu
				open={Boolean(state.optionButtonAnchor)}
				anchorEl={state.optionButtonAnchor}
				className="option-menu"
				onClose={() => {
					setState((prevState) => ({
						...prevState,
						optionButtonAnchor: null,
						selectedItemId: null,
					}));
				}}>
				<MenuItem
					onClick={onClickAskForDeletingFlightLog}
					className="normal-text">
					Delete
				</MenuItem>
			</Menu>
			<Dialog
				open={state.confirmDialogOpen}
				className="confirm-dialog"
				onClose={onClickCloseConfirmDialog}>
				<DialogContent>
					<Typography className="normal-text">
						Are you sure you want to delete?
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button className="dialog-button" onClick={onClickCloseConfirmDialog}>
						Oh, no thanks
					</Button>
					<Button className="dialog-button" onClick={onClickDeleteFlightLog}>
						Yes, I'm sure
					</Button>
				</DialogActions>
			</Dialog>
		</Grid2>
	);
}
